<!-- Console action feedback 0.1.155; 2026-10-28 18:11:13 CST; parent 44dbda7a05c960e3c79c6c322d676ac5e4982b20. -->
<!-- Console V0.1.130; 2026-10-02 20:51:43 CST; parent 5abb11ed04c30c2573a4557e316fd48d656507ca; CDN-RELIABILITY-20261002. -->
<!-- HotX Console 0.1.121 空态返回域名管理默认首页。实际修改时间：2026-09-29 13:25:25 CST；精确父基线：2b265b8475709018e4bf7099ec6a081871eb4c6b；变更：CONSOLE-HOME-DATA-PASSWORD-20260929。 -->
<script setup lang="ts">
import { ElMessage } from 'element-plus';
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import EmptyState from '@hotx-cdn/shared/components/EmptyState.vue';
import { consoleApi } from '../api/client';
import { setConsoleNavigationDirty } from '../router/navigationState';
import { publishCurrentDomainConfig } from '../utils/autoPublish';
import type { Domain, Origin, OriginCheckReport } from '../api/types';
import { displayDateTime } from '../utils/formatters';
import { buildOriginCreatePayload } from './originPayload';
import { validateOriginCompatibility } from './originCompatibility';

/*
 * Origin boundary:
 * This page owns customer origin configuration and origin health visibility.
 * Origin save must preserve publish/config delivery behavior and must not change cache, certificate, or security semantics.
 * Active failover/stale behavior is proven by backend + Agent + OpenResty, not by UI labels alone.
 */
const domains = ref<Domain[]>([]);
const origins = ref<Origin[]>([]);
const saving = ref(false);
const checkingId = ref<number>();
const selectedDomainId = ref<number>();
const error = ref('');
const success = ref('');
const advancedSections = ref<string[]>([]);
const editingOriginId = ref<number>();
const latestOriginCheck = ref<{
  taskId?: number;
  status?: string;
  originAddress?: string;
  originPort?: number;
  submittedAt: string;
}>();
const originReports = ref<OriginCheckReport[]>([]);
const form = reactive({
  originType: 'PRIMARY',
  originAddress: '',
  originPort: 80,
  host: '',
  tlsSni: '',
  protocol: 'HTTP',
  originPath: '/',
  weight: 100,
  priority: 100,
  connectTimeoutMs: 3000,
  readTimeoutMs: 30000,
  sendTimeoutMs: 3000
});
const canLoadOrigins = computed(() => Boolean(selectedDomainId.value));
const selectedDomain = computed(() => domains.value.find(domain => domain.id === selectedDomainId.value));
const totalOrigins = computed(() => origins.value.length);
const healthyOrigins = computed(() => origins.value.filter(origin => origin.healthStatus === 'HEALTHY').length);
const unhealthyOrigins = computed(() => origins.value.filter(origin => origin.healthStatus === 'UNHEALTHY').length);
const uncheckedOrigins = computed(() =>
  origins.value.filter(origin => !origin.healthStatus || origin.healthStatus === 'UNKNOWN').length
);

onMounted(async () => {
  try {
    domains.value = await consoleApi.domains.list();
    selectedDomainId.value = domains.value[0]?.id;
  } catch (e) {
    error.value = e instanceof Error ? e.message : '域名加载失败';
  }
});
onBeforeUnmount(() => setConsoleNavigationDirty(false));

watch(selectedDomainId, loadOrigins);
watch(form, () => {
  setConsoleNavigationDirty(Boolean(form.originAddress.trim() || form.host.trim() || form.tlsSni.trim()));
});

async function loadOrigins() {
  if (!selectedDomainId.value) return;
  try {
    error.value = '';
    origins.value = await consoleApi.origins.list(selectedDomainId.value);
    if (origins.value.length) {
      await loadOriginReports(origins.value[0].id);
    } else {
      originReports.value = [];
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : '源站列表加载失败';
  }
}

async function createOrigin() {
  if (saving.value || checkingId.value !== undefined) return;
  if (!selectedDomainId.value) {
    error.value = '请先选择域名';
    return;
  }
  if (!selectedDomain.value) {
    error.value = '请选择有效域名';
    return;
  }
  saving.value = true;
  try {
    error.value = '';
    success.value = '';
    const payload = buildOriginCreatePayload(form, selectedDomainId.value, selectedDomain.value.domain);
    validateOriginCompatibility(payload, origins.value, editingOriginId.value);
    if (editingOriginId.value) {
      await consoleApi.origins.update(editingOriginId.value, payload);
      success.value = '源站已更新';
    } else {
      await consoleApi.origins.create(payload);
      success.value = '源站已保存';
    }
    resetForm();
    setConsoleNavigationDirty(false);
    await loadOrigins();
    const publish = await publishCurrentDomainConfig(
      selectedDomainId.value,
      `客户保存 ${selectedDomain.value.domain} 源站后自动发布当前域名配置`
    );
    if (publish?.message) {
      success.value = `${success.value} ${publish.message}`;
    }
    if (publish && !publish.ok) ElMessage.warning(publish.message);
    else ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '源站保存失败';
    ElMessage.error(error.value);
  } finally { saving.value = false; }
}

function editOrigin(row: Origin) {
  editingOriginId.value = row.id;
  form.originType = row.originType || 'PRIMARY';
  form.originAddress = row.originAddress || '';
  form.originPort = row.originPort || 80;
  form.host = row.host || '';
  form.tlsSni = row.tlsSni || '';
  form.protocol = row.protocol || 'HTTP';
  form.originPath = row.originPath || '/';
  form.weight = row.weight ?? 100;
  form.priority = row.priority ?? 1;
  form.connectTimeoutMs = row.connectTimeoutMs ?? 3000;
  form.readTimeoutMs = row.readTimeoutMs ?? 30000;
  form.sendTimeoutMs = row.sendTimeoutMs ?? 3000;
}

function resetForm() {
  editingOriginId.value = undefined;
  form.originType = 'PRIMARY';
  form.originAddress = '';
  form.originPort = 80;
  form.host = '';
  form.tlsSni = '';
  form.protocol = 'HTTP';
  form.originPath = '/';
  form.weight = 100;
  form.priority = 100;
  form.connectTimeoutMs = 3000;
  form.readTimeoutMs = 30000;
  form.sendTimeoutMs = 3000;
}

async function checkOrigin(row: Origin) {
  if (saving.value || checkingId.value !== undefined) return;
  checkingId.value = row.id;
  try {
    error.value = '';
    success.value = '';
    const task = await consoleApi.origins.check(row.id) as { id?: number; status?: string };
    latestOriginCheck.value = {
      taskId: task.id,
      status: task.status || 'PENDING',
      originAddress: row.originAddress,
      originPort: row.originPort,
      submittedAt: new Date().toISOString()
    };
    success.value = `源站检测任务已提交${task.id ? ` #${task.id}` : ''}，当前状态：${task.status || '排队中'}。`;
    await loadOrigins();
    await loadOriginReports(row.id);
    if (success.value) ElMessage.success(success.value);
  } catch (e) {
    error.value = e instanceof Error ? e.message : '源站检测提交失败';
    ElMessage.error(error.value);
  } finally { checkingId.value = undefined; }
}

async function loadOriginReports(originId: number) {
  try {
    originReports.value = await consoleApi.origins.reports(originId);
  } catch {
    originReports.value = [];
  }
}

function healthText(status?: string) {
  const names: Record<string, string> = {
    HEALTHY: '正常',
    UNHEALTHY: '异常',
    UNKNOWN: '未检测',
    CHECKING: '检测中'
  };
  return names[status || ''] || status || '未检测';
}

function checkTaskStatusText(status?: string) {
  const names: Record<string, string> = {
    PENDING: '排队中',
    QUEUED: '排队中',
    RUNNING: '检测中',
    CHECKING: '检测中',
    SUCCEEDED: '已完成',
    HEALTHY: '正常',
    FAILED: '失败',
    UNHEALTHY: '异常'
  };
  return names[status || ''] || status || '排队中';
}

function reportHttpText(report: OriginCheckReport) {
  const status = report.httpStatus ?? report.statusCode;
  return status ? `HTTP ${status}` : 'HTTP -';
}

function reportLatencyText(report: OriginCheckReport) {
  const latency = report.latencyMs ?? report.responseTimeMs;
  return latency === undefined || latency === null ? '响应 -' : `响应 ${latency}ms`;
}

function reportMessage(report: OriginCheckReport) {
  return report.message || report.errorMessage || healthText(report.status);
}

function originTimeText(value: unknown) {
  return displayDateTime(value);
}
</script>

<template>
  <section class="page">
    <div class="page-title">
      <h1>源站管理</h1>
      <p>源站新增和查询调用真实 Origin Center；保存后由后端生成草稿版本。</p>
    </div>
    <el-alert v-if="error" :title="error" type="error" show-icon />
    <el-alert v-if="success" :title="success" type="success" show-icon />
    <el-select :disabled="saving || checkingId !== undefined" v-model="selectedDomainId" placeholder="选择域名" class="domain-select">
      <el-option v-for="domain in domains" :key="domain.id" :label="domain.domain" :value="domain.id" />
    </el-select>
    <section class="origin-summary-grid">
      <article class="origin-summary-card">
        <span>源站总数</span>
        <strong>{{ totalOrigins }}</strong>
      </article>
      <article class="origin-summary-card">
        <span>健康源站</span>
        <strong>{{ healthyOrigins }}</strong>
      </article>
      <article class="origin-summary-card">
        <span>异常源站</span>
        <strong>{{ unhealthyOrigins }}</strong>
      </article>
      <article class="origin-summary-card">
        <span>未检测源站</span>
        <strong>{{ uncheckedOrigins }}</strong>
      </article>
    </section>
    <section class="origin-workbench">
      <el-card class="form-card">
        <template #header>{{ editingOriginId ? '编辑源站' : '新增源站' }}</template>
        <el-form label-position="top">
          <el-form-item label="源站地址"><el-input v-model="form.originAddress" placeholder="origin.example.com" /></el-form-item>
          <el-form-item label="端口"><el-input-number v-model="form.originPort" :min="1" :max="65535" /></el-form-item>
          <el-form-item label="协议">
            <el-select v-model="form.protocol">
              <el-option label="HTTP" value="HTTP" />
              <el-option label="HTTPS" value="HTTPS" />
              <el-option label="HTTP+HTTPS" value="HTTP_HTTPS" />
            </el-select>
          </el-form-item>
          <el-collapse v-model="advancedSections" class="advanced-settings">
            <el-collapse-item title="高级设置" name="origin-advanced">
              <el-form-item label="回源 Host">
                <el-input v-model="form.host" placeholder="默认使用当前接入域名" />
                <p class="field-help">默认使用接入域名，只有源站需要特殊 Host 时才修改。</p>
              </el-form-item>
              <el-form-item label="TLS SNI">
                <el-input v-model="form.tlsSni" placeholder="HTTPS 回源默认使用回源 Host" />
                <p class="field-help">HTTPS 回源默认使用回源 Host，只有证书域名不一致时才修改。</p>
              </el-form-item>
            </el-collapse-item>
          </el-collapse>
          <div class="inline-control">
            <el-button type="primary" :disabled="!canLoadOrigins || saving || checkingId !== undefined" :loading="saving" @click="createOrigin">{{ saving ? '保存中…' : '保存源站' }}</el-button>
            <el-button v-if="editingOriginId" @click="resetForm">取消编辑</el-button>
          </div>
        </el-form>
      </el-card>
      <aside class="origin-guide-panel" aria-label="源站检测说明">
        <strong>源站检测说明</strong>
        <ul>
          <li>保存源站后点击“检测”确认源站健康状态。</li>
          <li>HTTP 200/301/302 通常代表源站可用。</li>
          <li>如果异常，请优先检查防火墙、端口、回源 Host 和 TLS SNI。</li>
        </ul>
      </aside>
    </section>
    <el-card v-if="latestOriginCheck" class="origin-check-card">
      <template #header>最近源站检测</template>
      <div class="origin-check-grid">
        <div>
          <span>检测对象</span>
          <strong>{{ latestOriginCheck.originAddress }}:{{ latestOriginCheck.originPort }}</strong>
        </div>
        <div>
          <span>任务 ID</span>
          <strong>任务 ID {{ latestOriginCheck.taskId || '-' }}</strong>
        </div>
        <div>
          <span>任务状态</span>
          <strong>{{ checkTaskStatusText(latestOriginCheck.status) }}</strong>
        </div>
        <div>
          <span>提交时间</span>
          <strong>{{ originTimeText(latestOriginCheck.submittedAt) }}</strong>
        </div>
      </div>
      <p class="status-help">如果持续异常，请检查源站防火墙、端口和回源 Host，再重新提交检测。</p>
    </el-card>
    <el-card v-if="originReports.length" class="origin-report-card">
      <template #header>最近检测报告</template>
      <div class="origin-report-list">
        <article v-for="report in originReports.slice(0, 3)" :key="report.id || report.checkedAt || report.createdAt" class="origin-report-row">
          <strong>{{ healthText(report.status) }}</strong>
          <span>{{ reportHttpText(report) }}</span>
          <span>{{ reportLatencyText(report) }}</span>
          <span>{{ originTimeText(report.checkedAt || report.createdAt) }}</span>
          <p>{{ reportMessage(report) }}</p>
        </article>
      </div>
    </el-card>
    <EmptyState v-if="!origins.length" title="暂无源站" description="请选择域名并新增真实源站。" home-to="/domains" />
    <el-table v-else :data="origins">
      <el-table-column prop="originAddress" label="地址" />
      <el-table-column prop="originPort" label="端口" width="100" />
      <el-table-column prop="protocol" label="协议" width="120" />
      <el-table-column prop="originType" label="类型" width="120" />
      <el-table-column prop="priority" label="优先级" width="100" />
      <el-table-column label="检测状态" width="140">
        <template #default="{ row }">{{ healthText(row.healthStatus) }}</template>
      </el-table-column>
      <el-table-column label="最近检测" width="180">
        <template #default="{ row }">{{ originTimeText(row.lastCheckAt) }}</template>
      </el-table-column>
      <el-table-column prop="lastError" label="最近错误" min-width="180" />
      <el-table-column label="操作" width="180">
        <template #default="{ row }">
          <el-button size="small" :loading="checkingId === row.id" :disabled="saving || checkingId !== undefined" @click="checkOrigin(row)">{{ checkingId === row.id ? '提交中…' : '检测' }}</el-button>
          <el-button size="small" @click="editOrigin(row)">编辑</el-button>
        </template>
      </el-table-column>
    </el-table>
  </section>
</template>

<style scoped>
.advanced-settings {
  margin-bottom: 16px;
}

.field-help {
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
  margin: 6px 0 0;
}

.origin-summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.origin-summary-card {
  display: grid;
  gap: 10px;
  border: 1px solid rgba(17, 60, 41, 0.1);
  border-radius: 16px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.86);
  box-shadow: 0 18px 45px rgba(25, 47, 34, 0.08);
}

.origin-summary-card span {
  color: #52675d;
  font-weight: 800;
}

.origin-summary-card strong {
  color: #1d2d25;
  font-size: 28px;
  line-height: 1;
}

.origin-workbench {
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(300px, 0.8fr);
  gap: 18px;
  align-items: stretch;
}

.origin-guide-panel {
  display: grid;
  align-content: start;
  gap: 14px;
  border: 1px solid rgba(17, 60, 41, 0.1);
  border-radius: 18px;
  padding: 22px;
  color: #244132;
  background:
    radial-gradient(circle at 92% 0%, rgba(242, 184, 91, 0.22), transparent 12rem),
    rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 45px rgba(25, 47, 34, 0.08);
}

.origin-guide-panel strong {
  color: #123f2a;
  font-size: 18px;
}

.origin-guide-panel ul {
  display: grid;
  gap: 12px;
  margin: 0;
  padding-left: 18px;
  line-height: 1.65;
}

.origin-check-card {
  margin: 18px 0;
}

.origin-check-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.origin-check-grid span {
  color: #64748b;
  display: block;
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 6px;
}

.origin-check-grid strong {
  color: #0f172a;
  word-break: break-all;
}

.origin-report-card {
  margin: 18px 0;
}

.origin-report-list {
  display: grid;
  gap: 10px;
}

.origin-report-row {
  display: grid;
  grid-template-columns: 1fr 110px 120px 180px;
  gap: 12px;
  align-items: center;
  border: 1px solid rgba(17, 60, 41, 0.1);
  border-radius: 14px;
  padding: 12px;
  background: #fbfdf9;
}

.origin-report-row strong {
  color: #123f2a;
}

.origin-report-row span {
  color: #52675d;
  font-weight: 700;
}

.origin-report-row p {
  grid-column: 1 / -1;
  margin: 0;
  color: #34483d;
}

@media (max-width: 980px) {
  .origin-summary-grid,
  .origin-workbench,
  .origin-check-grid,
  .origin-report-row {
    grid-template-columns: 1fr;
  }
}
</style>
